import { FC, useState } from 'react';

import { Input, Radio, message } from 'antd';
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { T } from 'react-translator-component';
import { NumericFormat } from 'react-number-format';

import { manualWalletAdjustment } from '../../../hooks/users';
import {
  FieldLabel,
  ManualAdjustmentModalStyled,
  OptionDescription,
  OptionText,
  OptionTitle,
} from './styled';

interface Props {
  open: boolean;
  userPk: number;
  onClose: () => void;
  onSuccess: () => void;
}

const ManualAdjustmentModal: FC<Props> = ({
  open,
  userPk,
  onClose,
  onSuccess,
}) => {
  const [amount, setAmount] = useState<number | null>(null);
  const [operation, setOperation] = useState<'ADD' | 'REMOVE'>('ADD');
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setAmount(null);
    setOperation('ADD');
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const handleConfirm = async () => {
    if (!amount || amount <= 0) {
      message.error(T('Enter a value greater than zero'));
      return;
    }
    setLoading(true);
    try {
      const res = await manualWalletAdjustment(userPk, { amount, operation });
      if (res?.status >= 200 && res?.status < 300) {
        message.success(T('Balance updated successfully'));
        reset();
        onSuccess();
        onClose();
      } else {
        message.error(T('Failed to update balance'));
      }
    } catch (e) {
      message.error(T('Failed to update balance'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
    <ManualAdjustmentModalStyled
      visible={open}
      centered
      width={460}
      title={T('Change statement value')}
      okText={T('Confirm')}
      cancelText={T('Cancel')}
      onOk={handleConfirm}
      onCancel={handleClose}
      confirmLoading={loading}
      destroyOnClose
    >
      <FieldLabel>{T('Value')} (R$)</FieldLabel>
      <NumericFormat
        customInput={Input}
        style={{ width: '100%', marginBottom: 24 }}
        thousandSeparator='.'
        decimalSeparator=','
        decimalScale={2}
        fixedDecimalScale
        allowNegative={false}
        placeholder={T('Ex: 10,00')}
        value={amount ?? ''}
        onValueChange={(values) => setAmount(values.floatValue ?? null)}
      />

      <FieldLabel>{T('Operation type')}</FieldLabel>
      <Radio.Group
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          marginTop: 16,
          width: '100%',
        }}
        onChange={(e) => setOperation(e.target.value)}
        value={operation}
      >
        <Radio value='ADD' style={{ display: 'flex', alignItems: 'flex-start' }}>
          <OptionText>
            <OptionTitle>
              <PlusCircleOutlined style={{ color: '#27ae60', fontSize: 20 }} />
              {T('Add value')}
            </OptionTitle>
            <OptionDescription>
              {T('The value will be added to the balance')}
            </OptionDescription>
          </OptionText>
        </Radio>

        <Radio value='REMOVE' style={{ display: 'flex', alignItems: 'flex-start' }}>
          <OptionText>
            <OptionTitle>
              <MinusCircleOutlined style={{ color: '#eb5757', fontSize: 20 }} />
              {T('Remove value')}
            </OptionTitle>
            <OptionDescription>
              {T('The value will be subtracted from the balance')}
            </OptionDescription>
          </OptionText>
        </Radio>
      </Radio.Group>
    </ManualAdjustmentModalStyled>
    </div>
  );
};

export default ManualAdjustmentModal;
